// src/services/ipfs.js
// PGT Ecosystem — Client-side Pinata IPFS Service
// Upload images & JSON metadata directly from the browser/mobile.
// No server dependency. Uses Pinata's public API with JWT auth.

const PINATA_API_URL = 'https://api.pinata.cloud';
const DEFAULT_GATEWAY = 'https://gateway.pinata.cloud';

/** Resolve Pinata JWT from any env style */
function getPinataJWT() {
  if (typeof process !== 'undefined' && process.env) {
    return (
      process.env.VITE_PINATA_JWT ||
      process.env.REACT_APP_PINATA_JWT ||
      process.env.PINATA_JWT ||
      ''
    );
  }
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      return import.meta.env.VITE_PINATA_JWT || '';
    }
  } catch (_) { /* ignore */ }
  return '';
}

/** Resolve Pinata gateway URL */
function getGatewayUrl() {
  if (typeof process !== 'undefined' && process.env) {
    return (
      process.env.VITE_PINATA_GATEWAY ||
      process.env.REACT_APP_PINATA_GATEWAY ||
      process.env.PINATA_GATEWAY ||
      DEFAULT_GATEWAY
    );
  }
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      return import.meta.env.VITE_PINATA_GATEWAY || DEFAULT_GATEWAY;
    }
  } catch (_) { /* ignore */ }
  return DEFAULT_GATEWAY;
}

// ---------------------------------------------------------------------------
// Upload helpers
// ---------------------------------------------------------------------------

/**
 * Upload a file (image, video, etc.) to IPFS via Pinata.
 * @param {File|Blob} file - The file to upload
 * @param {string} [name] - Optional filename for Pinata metadata
 * @param {Object} [metadata] - Optional key-value metadata for Pinata
 * @returns {Promise<{ipfsHash: string, pinSize: number, timestamp: string, url: string}>}
 */
export async function uploadFile(file, name, metadata = {}) {
  const jwt = getPinataJWT();
  if (!jwt) throw new Error('Pinata JWT not configured. Set VITE_PINATA_JWT in .env');

  const formData = new FormData();
  formData.append('file', file);

  const pinataMetadata = JSON.stringify({
    name: name || file.name || 'pgt-upload',
    keyvalues: { ecosystem: 'pgt', ...metadata },
  });
  formData.append('pinataMetadata', pinataMetadata);

  const pinataOptions = JSON.stringify({ cidVersion: 1 });
  formData.append('pinataOptions', pinataOptions);

  const res = await fetch(`${PINATA_API_URL}/pinning/pinFileToIPFS`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${jwt}` },
    body: formData,
  });

  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Pinata upload failed (${res.status}): ${err}`);
  }

  const data = await res.json();
  return {
    ipfsHash: data.IpfsHash,
    pinSize: data.PinSize,
    timestamp: data.Timestamp,
    url: `${getGatewayUrl()}/ipfs/${data.IpfsHash}`,
  };
}

/**
 * Upload JSON metadata to IPFS via Pinata (for NFT metadata).
 * @param {Object} json - The JSON object to upload
 * @param {string} [name] - Optional name for Pinata metadata
 * @param {Object} [metadata] - Optional key-value metadata for Pinata
 * @returns {Promise<{ipfsHash: string, pinSize: number, timestamp: string, url: string}>}
 */
export async function uploadJSON(json, name, metadata = {}) {
  const jwt = getPinataJWT();
  if (!jwt) throw new Error('Pinata JWT not configured. Set VITE_PINATA_JWT in .env');

  const body = {
    pinataContent: json,
    pinataMetadata: {
      name: name || 'pgt-metadata.json',
      keyvalues: { ecosystem: 'pgt', type: 'metadata', ...metadata },
    },
    pinataOptions: { cidVersion: 1 },
  };

  const res = await fetch(`${PINATA_API_URL}/pinning/pinJSONToIPFS`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${jwt}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Pinata JSON upload failed (${res.status}): ${err}`);
  }

  const data = await res.json();
  return {
    ipfsHash: data.IpfsHash,
    pinSize: data.PinSize,
    timestamp: data.Timestamp,
    url: `${getGatewayUrl()}/ipfs/${data.IpfsHash}`,
  };
}

/**
 * Upload NFT ticket metadata: image first, then JSON with image CID.
 * @param {File|Blob} imageFile - Ticket image
 * @param {Object} ticketMetadata - { name, description, eventId, ticketTypeId, eventDate, venue, attributes }
 * @returns {Promise<{metadataUrl: string, imageUrl: string, metadataHash: string, imageHash: string}>}
 */
export async function uploadTicketMetadata(imageFile, ticketMetadata) {
  // 1. Upload image
  const imageResult = await uploadFile(
    imageFile,
    `ticket-${ticketMetadata.eventId || 'event'}-image`,
    { type: 'ticket-image', eventId: String(ticketMetadata.eventId || '') }
  );

  // 2. Build ERC-1155 / OpenSea-compatible metadata
  const metadata = {
    name: ticketMetadata.name || 'PGT Event Ticket',
    description: ticketMetadata.description || '',
    image: imageResult.url,
    external_url: ticketMetadata.externalUrl || '',
    attributes: ticketMetadata.attributes || [],
    properties: {
      eventId: ticketMetadata.eventId,
      ticketTypeId: ticketMetadata.ticketTypeId,
      eventDate: ticketMetadata.eventDate,
      venue: ticketMetadata.venue,
      isUsed: false,
    },
  };

  // 3. Upload metadata JSON
  const metaResult = await uploadJSON(
    metadata,
    `ticket-${ticketMetadata.eventId || 'event'}-metadata`,
    { type: 'ticket-metadata', eventId: String(ticketMetadata.eventId || '') }
  );

  return {
    metadataUrl: metaResult.url,
    imageUrl: imageResult.url,
    metadataHash: metaResult.ipfsHash,
    imageHash: imageResult.ipfsHash,
  };
}

/**
 * Upload event banner/cover image.
 * @param {File|Blob} imageFile
 * @param {string|number} eventId
 * @returns {Promise<{ipfsHash: string, url: string}>}
 */
export async function uploadEventImage(imageFile, eventId) {
  return uploadFile(imageFile, `event-${eventId}-banner`, {
    type: 'event-image',
    eventId: String(eventId),
  });
}

// ---------------------------------------------------------------------------
// Fetch / resolve helpers
// ---------------------------------------------------------------------------

/**
 * Resolve an IPFS URI to an HTTP gateway URL.
 * Handles: ipfs://Qm..., ipfs://bafy..., /ipfs/Qm..., plain CID
 * @param {string} uri
 * @returns {string}
 */
export function resolveIPFS(uri) {
  if (!uri) return '';
  const gateway = getGatewayUrl();

  if (uri.startsWith('ipfs://')) {
    return `${gateway}/ipfs/${uri.slice(7)}`;
  }
  if (uri.startsWith('/ipfs/')) {
    return `${gateway}${uri}`;
  }
  if (uri.startsWith('Qm') || uri.startsWith('bafy')) {
    return `${gateway}/ipfs/${uri}`;
  }
  // Already a full URL
  return uri;
}

/**
 * Fetch and parse JSON metadata from an IPFS URI.
 * @param {string} uri - IPFS or HTTP URI
 * @returns {Promise<Object>}
 */
export async function fetchMetadata(uri) {
  const url = resolveIPFS(uri);
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch metadata from ${url}: ${res.status}`);
  return res.json();
}

// ---------------------------------------------------------------------------
// Pinata account info (optional — for checking quota)
// ---------------------------------------------------------------------------

/** Check Pinata account pin usage */
export async function getPinataUsage() {
  const jwt = getPinataJWT();
  if (!jwt) return null;

  const res = await fetch(`${PINATA_API_URL}/data/userPinnedDataTotal`, {
    headers: { Authorization: `Bearer ${jwt}` },
  });
  if (!res.ok) return null;
  return res.json();
}

/** Test Pinata authentication */
export async function testPinataAuth() {
  const jwt = getPinataJWT();
  if (!jwt) return { ok: false, error: 'No JWT configured' };

  try {
    const res = await fetch(`${PINATA_API_URL}/data/testAuthentication`, {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    if (res.ok) {
      const data = await res.json();
      return { ok: true, message: data.message };
    }
    return { ok: false, error: `HTTP ${res.status}` };
  } catch (err) {
    return { ok: false, error: err.message };
  }
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export default {
  // Upload
  uploadFile,
  uploadJSON,
  uploadTicketMetadata,
  uploadEventImage,
  // Fetch
  resolveIPFS,
  fetchMetadata,
  // Account
  getPinataUsage,
  testPinataAuth,
  // Config
  getGatewayUrl,
};
